import React from 'react';
import { formatBusiness } from 'config/time';
import user_logo from 'assets/icons/user.svg';
import { IconEdit } from '@tabler/icons-react';
import { Attendances } from './AttendanceField';
import { useTranslation } from 'react-i18next';
import DeviceTimeZone, { deviceTimeZoneOf, isAdminEnteredEnd } from 'reusable/DeviceTimeZone';
import { durationText, EMPTY_DURATION } from 'reusable/durationText';

interface AttendanceTableProps {
  attendances: Attendances[];
  onEdit: (attendanceId: string) => void;
}

const AttendanceTable: React.FC<AttendanceTableProps> = ({
  attendances,
  onEdit,
}) => {
  const { t } = useTranslation(['attendance', 'common']);
  const formatDate = (initialDate: any) =>
    // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
    formatBusiness(initialDate, t('table.date-format'));

  // EV-312（2026-10-13 PM 判断: 時間は「時・分・秒」、空の値は「-」）。英語は時・分の単複を出し分ける
  // （common の ui.duration-hm・ui.duration-m・ui.duration-h。以前は table.hours・table.mins の固定の複数形で
  // 「1 hrs 1 mins」、日本語は「1 時間 1 分」とほかの画面と形が違った）。数でなければ「-」。
  const hoursMinutes = (hours: number, minutes: number) =>
    durationText(t, 'ui.duration-hm', { hours, minutes }, { ns: 'common' });
  const minutesOnly = (minutes: number) =>
    durationText(t, 'ui.duration-m', { minutes }, { ns: 'common' });
  const hoursOnly = (hours: number) =>
    durationText(t, 'ui.duration-h', { hours }, { ns: 'common' });

  const formatTime = (time: string) => {
    const [hrs, mins] = time.split(':');
    const hours = parseInt(hrs, 10);
    const minutes = parseInt(mins, 10);

    return hours === 0 ? minutesOnly(minutes) : hoursMinutes(hours, minutes);
  };

  function calculateDuration(
    check_in: string | Date | null,
    check_out: string | Date | null
  ): string {
    // EV-312。退勤が無い行は「-」（以前は null を 1970年として比べ、負の差で「0 mins」と出していた）。
    if (!check_in || !check_out) return EMPTY_DURATION;
    // Calculate the difference in milliseconds
    const checkIn = new Date(check_in);
    const checkOut = new Date(check_out);

    // 日時が読めないと NaN になり「NaN hrs NaN mins」と出ていた（今は durationText が「-」にする）。
    const durationMs = checkOut.getTime() - checkIn.getTime();

    // Convert milliseconds to hours, minutes, and seconds
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    // Return the duration as a formatted string
    if (durationMs < 60000) {
      return minutesOnly(0);
    } else if (hours === 0) {
      return minutes === 0 ? '-' : minutesOnly(minutes);
    } else {
      return minutes === 0 ? hoursOnly(hours) : hoursMinutes(hours, minutes);
    }
  }

  // EV-330（2026-10-14 PM 判断: 同じ運転者が続く行にも、名前を薄く出す）。続く行はアイコン・電話番号を省き、
  // 名前だけを薄い色で出す（以前は何も出さず、省いたことが見た目でも読み上げでも分からなかった）。
  let lastDisplayedDriver: { name?: string; phone?: string } | null = null;

  return (
    <main className="flex flex-col my-6">
      <div className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-4 px-2 py-2.5 bg-primary/50 rounded-md w-full text-caption">
        <div className="col-span-2">{t('table.driver-name')}</div>
        <div className="col-span-2">{t('table.checkin')}</div>
        <div className="col-span-2">{t('table.checkout')}</div>
        <div className="col-span-2 text-center">{t('table.duration')}</div>
        <div className="col-span-2 text-center">{t('table.overtime')}</div>
        <div className="col-span-2 text-center">{t('time-zone.device', { ns: 'common' })}</div>
        <div className="col-span-1 text-center">{t('table.action')}</div>
      </div>
      {attendances.length === 0 ? (
        <div className="text-center my-4">{t('table.no-data')}</div>
      ) : (
        attendances.map((attendance, index) => {
          // EV-330。user が null の行（運転者が紐づかない記録）でも表を落とさない（名前・電話番号は「-」）。
          const displayDriver =
            !lastDisplayedDriver ||
            lastDisplayedDriver.name !== attendance.user?.name ||
            lastDisplayedDriver.phone !== attendance.user?.phone;

          if (displayDriver) {
            lastDisplayedDriver = {
              name: attendance.user?.name,
              phone: attendance.user?.phone,
            };
          }

          return (
            <div
              key={index}
              className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-4 px-2 py-1 sm:min-h-8 md:min-h-12 lg:min-h-16 border-b text-caption"
            >
              <div className="col-span-2 flex space-x-2 items-center">
                {displayDriver ? (
                  <>
                    <img
                      src={attendance.user?.profile_picture || user_logo}
                      alt=""
                      className="h-10 w-10 object-cover rounded-full"
                    />
                    <div className="w-full truncate">
                      <div className="text-body-2 text-neutral/300 truncate">
                        {attendance.user?.name || '-'}
                      </div>
                      {/* EV-289。電話番号の行。無いときはメールアドレスに落とさない（以前は API が phone を返さず、いつもメールアドレスが出ていた）。 */}
                      {/* EV-303。空は同じ表のほかの欄（退勤・残業）と同じ「-」（以前は「N/A」）。 */}
                      <div className="text-caption text-neutral/100 truncate">
                        {attendance.user?.phone || '-'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* アイコンの幅を空けて、名前の位置を最初の行とそろえる。 */}
                    <span className="h-10 w-10 shrink-0" aria-hidden="true" />
                    <div className="w-full truncate">
                      <div className="text-body-2 text-neutral/100 truncate">
                        {attendance.user?.name || '-'}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="col-span-2 flex items-center">
                {formatDate(attendance.check_in)}
              </div>
              <div className="col-span-2 flex items-center">
                {attendance.check_out ? formatDate(attendance.check_out) : '-'}
              </div>

              <div className="col-span-2 flex items-center justify-center text-center">
                {calculateDuration(attendance.check_in, attendance.check_out)}
              </div>

              <div
                className={`col-span-2 flex items-center justify-center text-center
                ${
                  attendance.overtime_duration
                    ? 'text-error'
                    : 'text-neutral/300'
                }
              `}
              >
                {attendance.overtime_duration
                  ? formatTime(attendance.overtime_duration)
                  : '-'}
              </div>
              {/* EV-116 段階3。出勤・退勤の時点の端末の時間帯（同じ列に2つ。既存の見出しの文言で区別）。 */}
              <div className="col-span-2 flex flex-col justify-center text-center">
                <DeviceTimeZone {...deviceTimeZoneOf(attendance)} at={attendance.check_in} label={t('table.checkin')} />
                {attendance.check_out && (
                  <DeviceTimeZone
                    {...deviceTimeZoneOf(attendance, 'check_out_')} adminEntered={isAdminEnteredEnd(attendance, 'check_out_')}
                    at={attendance.check_out}
                    label={t('table.checkout')}
                  />
                )}
              </div>
              <div className="col-span-1 flex items-center justify-center gap-3 cursor-pointer">
                <button
                  type="button"
                  className="inline-flex items-center"
                  aria-label={t('ui.edit', { ns: 'common' })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(attendance.id);
                  }}
                >
                  <IconEdit
                    stroke={1.5}
                    className="text-neutral/100 size-5"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          );
        })
      )}
    </main>
  );
};
export default AttendanceTable;
